"use client";

import { SHARED_AUTH_APP_HEADER, type SharedAuthApp } from "@newform/constants";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { type ReactNode, type Ref, useEffect, useState } from "react";
import { GoogleIcon } from "@/components/Icons";
import { Alert, AlertDescription } from "@/components/ui/design-system/alert";
import { Button } from "@/components/ui/design-system/button";
import { CodeField } from "@/components/ui/design-system/code-field";
import { Input } from "@/components/ui/design-system/input";
import { Label } from "@/components/ui/design-system/label";
import { Body, Display } from "@/components/ui/design-system/text";
import { sharedAuthClient } from "@/lib/sharedAuthClient";
import { cn } from "@/lib/utils";

/**
 * The six digits, as a constant rather than three literals.
 */
export const SIGN_IN_CODE_LENGTH = 6;

/**
 * Everything the form says. NewForm One passes its translated catalogue
 * (CreatorSignIn), Framework its English copy (app/sign-in), so the form
 * itself holds no strings.
 */
export interface SignInCopy {
  headline: ReactNode;
  lede: ReactNode;
  checkEmail: ReactNode;
  codeSent: (email: string) => ReactNode;
  emailLabel: string;
  emailPlaceholder: string;
  emailSubmit: string;
  google: string;
  /** Under the email step, when the lede doesn't already say a code is coming. */
  codeHint?: string;
  codeLabel: string;
  signingIn: string;
  resendCountdown: (seconds: number) => string;
  resend: string;
  differentEmail: string;
  errors: {
    googleFinish: string;
    googleStart: string;
    sendFailed: string;
    codeRejected: string;
    verifyFailed: string;
    offline: string;
  };
}

/** A text action: the resend, the way back, a switch between sign-in kinds. */
export const SIGN_IN_LINK = cn(
  "inline-flex cursor-pointer items-center gap-nf-ds-2 rounded-nf-ds-button",
  "font-nf-ds-sans text-nf-ds-sm font-medium",
  "outline-hidden transition-colors duration-150 ease-nf-ds-out",
  "focus-visible:ring-2 focus-visible:ring-nf-ds-brand",
  "disabled:pointer-events-none disabled:opacity-50",
  "active:opacity-70 motion-reduce:transition-none",
);

/**
 * Better Auth sign-in by emailed code, or Google: the one sign-in form for
 * NewForm One and Framework. The caller owns the frame around it and what
 * happens once the person is signed in (`onSignedIn`).
 *
 * `app` names which app is asking, so the code email carries that app's
 * name (SHARED_AUTH_APP_HEADER). `idPrefix` keeps the field ids stable per
 * surface. `notice` renders between the header and the form, `footer` below
 * it. `onPaper` is for a frame on the cream page background, where muted text
 * needs its contrast-safe role.
 */
export function SignInForm({
  app,
  copy,
  idPrefix,
  initialEmail = "",
  googleEnabled,
  notice,
  footer,
  contentRef,
  onPaper = false,
  onSignedIn,
}: {
  app: SharedAuthApp;
  copy: SignInCopy;
  idPrefix: string;
  initialEmail?: string;
  googleEnabled: boolean;
  notice?: ReactNode;
  footer?: ReactNode;
  /** The block that hugs the content, for a frame that measures it. */
  contentRef?: Ref<HTMLDivElement>;
  onPaper?: boolean;
  onSignedIn: () => Promise<void>;
}) {
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState("");
  const [requested, setRequested] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);
  /**
   * Bumped on every rejected code, to remount the field.
   *
   * Clearing the value empties the cells but leaves the caret wherever it
   * was, which on a rejection is the last one — so the reader starts typing
   * into the sixth box. Remounting re-runs the field's own autofocus and puts
   * them back at the first.
   */
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    const callback = new URL(window.location.href);
    if (!callback.searchParams.has("error")) return;
    setError(copy.errors.googleFinish);
    callback.searchParams.delete("error");
    window.history.replaceState(window.history.state, "", callback);
  }, []);
  useEffect(() => {
    if (!cooldown) return;
    const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  async function sendCode() {
    setPending(true);
    setError(null);
    try {
      const result = await sharedAuthClient.emailOtp.sendVerificationOtp({
        email: email.trim().toLowerCase(),
        type: "sign-in",
        fetchOptions: { headers: { [SHARED_AUTH_APP_HEADER]: app } },
      });
      if (result.error) {
        setError(result.error.message ?? copy.errors.sendFailed);
        return;
      }
      setRequested(true);
      setCode("");
      setCooldown(60);
    } catch {
      setError(copy.errors.offline);
    } finally {
      setPending(false);
    }
  }
  /**
   * `otp` defaults to state for the button path, and is passed explicitly by
   * the auto-submit. `onComplete` fires from inside the field's write, before
   * React has re-rendered, so reading `code` from the closure there sends the
   * value from *before* the last digit — an empty string on the first try.
   */
  async function verify(otp: string = code) {
    setPending(true);
    setError(null);
    try {
      const result = await sharedAuthClient.signIn.emailOtp({
        email: email.trim().toLowerCase(),
        otp,
      });
      if (result.error) {
        setError(result.error.message ?? copy.errors.codeRejected);
        // Empty the cells. Six digits already rejected are six digits to
        // delete before the next attempt, and leaving them there invites
        // re-submitting the same code.
        setCode("");
        setAttempt((n) => n + 1);
        return;
      }
      await onSignedIn();
    } catch {
      setError(copy.errors.verifyFailed);
      setCode("");
      setAttempt((n) => n + 1);
    } finally {
      setPending(false);
    }
  }
  async function google() {
    setPending(true);
    setError(null);
    try {
      const callback = new URL(window.location.href);
      callback.searchParams.delete("error");
      const result = await sharedAuthClient.signIn.social({
        provider: "google",
        callbackURL: callback.toString(),
        errorCallbackURL: callback.toString(),
      });
      if (result.error)
        setError(result.error.message ?? copy.errors.googleStart);
    } catch {
      setError(copy.errors.googleStart);
    } finally {
      setPending(false);
    }
  }

  const codeId = `${idPrefix}-code`;
  const hintId = `${idPrefix}-code-hint`;
  const emailId = `${idPrefix}-email`;
  const mutedText = onPaper
    ? "text-nf-ds-fg-muted-on-paper"
    : "text-nf-ds-fg-muted";

  return (
    <div ref={contentRef} className="flex flex-col gap-nf-ds-6">
      <header className="flex min-w-0 flex-col items-center gap-nf-ds-2 text-center">
        <Display as="h1" className="text-balance">
          {requested ? copy.checkEmail : copy.headline}
        </Display>
        <Body muted onPaper={onPaper} className="text-pretty">
          {requested ? copy.codeSent(email.trim()) : copy.lede}
        </Body>
      </header>

      {notice}

      <form
        className="flex w-full flex-col items-center gap-nf-ds-4"
        onSubmit={(event) => {
          event.preventDefault();
          void (requested ? verify() : sendCode());
        }}
      >
        {requested ? (
          <>
            <div className="flex w-full flex-col gap-nf-ds-2">
              {/* A span, not a label: six inputs have no single target for
                  `htmlFor`, and CodeField's fieldset already carries the
                  group's accessible name. `asChild` keeps the typography
                  without the dangling element. */}
              <Label asChild>
                <span>{copy.codeLabel}</span>
              </Label>
              <CodeField
                key={attempt}
                id={codeId}
                value={code}
                onChange={(next) => {
                  setCode(next);
                  // Clear the rejection as soon as the reader edits. A code
                  // marked wrong while they fix it is a stale accusation.
                  if (error) setError(null);
                }}
                length={SIGN_IN_CODE_LENGTH}
                autoFocus
                invalid={Boolean(error)}
                describedBy={hintId}
                // The sixth digit submits. Asking someone to confirm a
                // code they have just finished typing is a step that
                // exists only because the form has a button. A rejection
                // lands where it would have anyway, and editing a digit
                // clears it and arms the next attempt.
                //
                // Guarded on `pending` so a paste landing mid-request
                // cannot fire a second verify.
                onComplete={(full) => {
                  if (pending) return;
                  void verify(full);
                }}
              />
              <div
                id={hintId}
                className="flex min-h-nf-ds-8 items-center justify-center"
              >
                {pending ? (
                  <Body muted onPaper={onPaper} className="text-nf-ds-sm">
                    {copy.signingIn}
                  </Body>
                ) : cooldown > 0 ? (
                  <Body muted onPaper={onPaper} className="text-nf-ds-sm">
                    {copy.resendCountdown(cooldown)}
                  </Body>
                ) : (
                  <button
                    type="button"
                    disabled={pending}
                    onClick={() => void sendCode()}
                    className={cn(
                      SIGN_IN_LINK,
                      "px-nf-ds-2 text-nf-ds-fg underline decoration-nf-ds-fg/30 underline-offset-2",
                    )}
                  >
                    {copy.resend}
                  </button>
                )}
              </div>
            </div>

            <button
              type="button"
              disabled={pending}
              onClick={() => {
                setRequested(false);
                setCode("");
                setError(null);
              }}
              className={cn(SIGN_IN_LINK, "px-nf-ds-2", mutedText)}
            >
              <ArrowLeft aria-hidden="true" className="size-4" />
              {copy.differentEmail}
            </button>
          </>
        ) : (
          <>
            {/* The submit sits inside the field. There is exactly one thing
                to do with an address here, so a second full-width button
                below it earned nothing. */}
            <div className="relative w-full">
              <Label className="sr-only" htmlFor={emailId}>
                {copy.emailLabel}
              </Label>
              <Input
                id={emailId}
                type="email"
                autoComplete="email"
                required
                maxLength={254}
                placeholder={copy.emailPlaceholder}
                value={email}
                disabled={pending}
                onChange={(event) => setEmail(event.target.value)}
                className="pr-nf-ds-12"
              />
              <button
                type="submit"
                disabled={pending || !email.trim()}
                aria-label={copy.emailSubmit}
                className={cn(
                  "absolute inset-y-0 right-nf-ds-2 my-auto",
                  "inline-flex size-8 cursor-pointer items-center justify-center rounded-nf-ds-button",
                  "text-nf-ds-fg-muted outline-hidden",
                  "transition-[color,background-color] duration-150 ease-nf-ds-out",
                  "focus-visible:ring-2 focus-visible:ring-nf-ds-brand",
                  "disabled:pointer-events-none disabled:opacity-40",
                  "motion-reduce:transition-none",
                )}
              >
                <ArrowRight aria-hidden="true" className="size-4" />
              </button>
            </div>

            {/* Ghost, not filled. Two buttons of equal weight ask the
                reader to choose before telling them the difference — which
                is what retired the "or" rule — but a bare text link
                under-sells a real alternative. A body that appears on hover
                says "this is a control" without competing with the one
                action the screen is for. */}
            {googleEnabled && (
              <Button
                type="button"
                variant="ghost"
                disabled={pending}
                onClick={() => void google()}
                className="gap-nf-ds-2"
              >
                <GoogleIcon className="size-4" />
                {copy.google}
              </Button>
            )}

            {copy.codeHint && (
              <Body
                muted
                onPaper={onPaper}
                className="text-nf-ds-sm text-pretty"
              >
                {copy.codeHint}
              </Body>
            )}
          </>
        )}

        {error && (
          <Alert variant="err" role="alert" className="w-full">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
      </form>

      {footer}
    </div>
  );
}
